import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import posthog from '../utils/posthog-stub'; // FERPA: Using no-op stub until self-hosted PostHog is configured
import Branding from './Branding';

// PostHog initialization disabled for FERPA compliance
// Will be replaced with self-hosted instance later
// See: https://posthog.com/docs/getting-started/install

const AILearningLabLanding = () => {
  const [searchParams] = useSearchParams();
  const [selectedPricing, setSelectedPricing] = useState('full');

  // Extract UTM parameters
  const utmSource = searchParams.get('utm_source') || 'direct';
  const utmCampaign = searchParams.get('utm_campaign') || 'learning-lab';
  const utmMedium = searchParams.get('utm_medium') || 'organic';

  useEffect(() => {
    // Track landing page visit
    posthog.capture('learning_lab_landing_visited', {
      utm_source: utmSource,
      utm_campaign: utmCampaign,
      utm_medium: utmMedium,
      page_url: window.location.href
    });
  }, [utmSource, utmCampaign, utmMedium]);

  const handlePricingSelect = (pricing) => {
    posthog.capture('learning_lab_pricing_selected', {
      pricing_tier: pricing,
      utm_source: utmSource
    });
    setSelectedPricing(pricing);
  };

  const handleRegisterClick = (tier) => {
    posthog.capture('learning_lab_register_clicked', {
      pricing_tier: tier,
      utm_source: utmSource
    });
  };

  const pricingOptions = {
    full: {
      name: 'Full Program',
      price: '$299',
      description: '8-week AI literacy program + full year Mira access',
      features: [
        '8 weekly coaching sessions (90 min each)',
        'Learn to learn with AI - future-ready skills',
        'Full year of Mira web app included ($89 value)',
        'Small groups (5 students max)',
        'Certificate of completion',
        'Math & Reading focus'
      ],
      stripeLin: 'https://checkout.teachtappy.com/b/00wdR86T4aOd4pR6m8frW0o',
      badge: 'Most Popular',
      highlight: true
    },
    community: {
      name: 'Community Pricing',
      price: '$149',
      description: 'Same program, accessible pricing for qualifying families',
      features: [
        'All Full Program features included',
        'For families receiving free/reduced lunch',
        'EBT/SNAP benefits',
        'Housing assistance',
        'Same quality, accessible rate'
      ],
      stripeLink: 'https://checkout.teachtappy.com/b/14A14m0uG09z7C35i4frW0p',
      badge: 'Community Rate',
      highlight: false
    },
    scholarship: {
      name: 'Scholarship Rate',
      price: '$99',
      description: 'Limited scholarship spots available',
      features: [
        'All Full Program features included',
        'Limited availability',
        'Based on need',
        'Simple application process'
      ],
      stripeLink: 'https://checkout.teachtappy.com/b/eVq4gyfpAf4tf4vaCofrW0q',
      badge: 'Limited Spots',
      highlight: false
    }
  };

  const selected = pricingOptions[selectedPricing];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #FAFAFA 0%, #F1F5F9 100%)',
      padding: '1rem'
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center', padding: '2rem 0 1rem' }}>
        <img
          src="/images/TAPPY-logo-color-1.png"
          alt="Tappy Labs"
          style={{ height: '48px', marginBottom: '1rem' }}
        />
      </div>

      {/* Main Content */}
      <div style={{
        maxWidth: '1000px',
        margin: '0 auto',
        background: 'white',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.1)'
      }}>

        {/* Hero Section */}
        <div style={{
          background: 'linear-gradient(135deg, #0EA5E9 0%, #0F172A 100%)',
          color: 'white',
          padding: '3rem 2rem',
          textAlign: 'center'
        }}>
          <h1 style={{
            fontSize: 'clamp(28px, 5vw, 42px)',
            fontWeight: '700',
            marginBottom: '1rem',
            lineHeight: 1.2
          }}>
            AI Learning Lab
          </h1>
          <p style={{
            fontSize: 'clamp(18px, 3vw, 22px)',
            marginBottom: '1.5rem',
            lineHeight: 1.4,
            fontWeight: '400'
          }}>
            Teaching Students HOW to Learn with AI
          </p>
          <div style={{
            background: 'rgba(255, 255, 255, 0.15)',
            padding: '1rem',
            borderRadius: '12px',
            marginBottom: '1rem',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <p style={{
              margin: 0,
              fontSize: 'clamp(14px, 2.5vw, 16px)',
              fontWeight: '500'
            }}>
              8-week program • Middle & High School • Small Groups (5 max)
            </p>
          </div>
          <p style={{
            fontSize: '14px',
            opacity: 0.95,
            margin: 0
          }}>
            Lutz, FL
          </p>
        </div>

        {/* Program Details */}
        <div style={{ padding: '2rem' }}>

          {/* What Parents Notice */}
          <div style={{ marginBottom: '3rem' }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '700',
              marginBottom: '1.5rem',
              color: '#1a1a1a',
              textAlign: 'center'
            }}>
              What Parents Notice
            </h2>

            <div style={{
              display: 'grid',
              gap: '1rem',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              marginBottom: '2rem'
            }}>
              <div style={{
                padding: '1.5rem',
                background: '#f8f9fa',
                borderRadius: '12px',
                border: '1px solid #e5e7eb'
              }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '0.5rem', color: '#1a1a1a' }}>
                  Homework Gets Easier
                </h3>
                <p style={{ fontSize: '14px', color: '#666', margin: 0, lineHeight: 1.5 }}>
                  Students learn to ask AI better questions and understand concepts more deeply
                </p>
              </div>

              <div style={{
                padding: '1.5rem',
                background: '#f8f9fa',
                borderRadius: '12px',
                border: '1px solid #e5e7eb'
              }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '0.5rem', color: '#1a1a1a' }}>
                  Grades Improve
                </h3>
                <p style={{ fontSize: '14px', color: '#666', margin: 0, lineHeight: 1.5 }}>
                  Better understanding leads to better results in math, reading, and other subjects
                </p>
              </div>

              <div style={{
                padding: '1.5rem',
                background: '#f8f9fa',
                borderRadius: '12px',
                border: '1px solid #e5e7eb'
              }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '0.5rem', color: '#1a1a1a' }}>
                  Kids Are More Confident
                </h3>
                <p style={{ fontSize: '14px', color: '#666', margin: 0, lineHeight: 1.5 }}>
                  They know how to learn anything, giving them skills for college and careers
                </p>
              </div>
            </div>

            {/* This Isn't Tutoring */}
            <div style={{
              background: 'linear-gradient(135deg, #ECF3F2 0%, #D5DEDD 100%)',
              padding: '1.5rem',
              borderRadius: '12px',
              border: '2px solid #3b82b8'
            }}>
              <h3 style={{
                margin: '0 0 0.5rem 0',
                fontSize: '18px',
                fontWeight: '700',
                color: '#1a1a1a',
                textAlign: 'center'
              }}>
                This Isn't Tutoring
              </h3>
              <p style={{
                margin: 0,
                fontSize: '15px',
                fontWeight: '500',
                color: '#1a1a1a',
                textAlign: 'center',
                lineHeight: 1.6
              }}>
                We teach students how to work with AI to learn better and think critically. It's like technology literacy for the AI era.
              </p>
            </div>
          </div>

          {/* Schedule */}
          <div style={{ marginBottom: '3rem' }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '700',
              marginBottom: '1.5rem',
              color: '#1a1a1a',
              textAlign: 'center'
            }}>
              Program Schedule
            </h2>

            <div style={{
              background: '#f8f9fa',
              padding: '1.5rem',
              borderRadius: '12px',
              marginBottom: '1rem'
            }}>
              <div style={{ marginBottom: '1rem' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '0.5rem', color: '#1a1a1a' }}>
                  Choose One Weekly Time
                </h3>
                <div style={{ fontSize: '15px', color: '#666', lineHeight: 1.8 }}>
                  <p style={{ margin: '0.5rem 0' }}>• <strong>Tuesday/Thursday:</strong> 4:30pm - 6:00pm</p>
                  <p style={{ margin: '0.5rem 0' }}>• <strong>Saturday:</strong> 10:00am - 11:30am</p>
                  <p style={{ margin: '0.5rem 0' }}>• <strong>Saturday:</strong> 1:00pm - 2:30pm</p>
                </div>
              </div>

              <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                <p style={{ fontSize: '15px', color: '#666', margin: '0 0 0.5rem 0' }}>
                  <strong>Location:</strong> 18311 N US Hwy 41, Lutz, FL 33549
                </p>
                <p style={{ fontSize: '15px', color: '#666', margin: 0 }}>
                  <strong>What to bring:</strong> Laptop (preferred) or tablet
                </p>
              </div>
            </div>
          </div>

          {/* Pricing Selection */}
          <div style={{ marginBottom: '2rem' }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '700',
              marginBottom: '1.5rem',
              color: '#1a1a1a',
              textAlign: 'center'
            }}>
              Choose Your Investment
            </h2>

            <div style={{
              display: 'grid',
              gap: '1rem',
              marginBottom: '2rem',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))'
            }}>
              {Object.keys(pricingOptions).map(key => {
                const option = pricingOptions[key];
                return (
                  <button
                    key={key}
                    onClick={() => handlePricingSelect(key)}
                    style={{
                      padding: '1.5rem',
                      border: selectedPricing === key ? '2px solid #0EA5E9' : '2px solid #e5e7eb',
                      borderRadius: '12px',
                      background: selectedPricing === key ? 'rgba(14, 165, 233, 0.05)' : 'white',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.2s ease',
                      position: 'relative'
                    }}
                  >
                    {option.badge && (
                      <div style={{
                        position: 'absolute',
                        top: '-10px',
                        right: '10px',
                        background: option.highlight ? '#0EA5E9' : '#64748B',
                        color: 'white',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600'
                      }}>
                        {option.badge}
                      </div>
                    )}
                    <div style={{ fontSize: '32px', fontWeight: '700', marginBottom: '0.5rem', color: '#0EA5E9' }}>
                      {option.price}
                    </div>
                    <div style={{ fontWeight: '600', marginBottom: '0.25rem', fontSize: '18px', color: '#1a1a1a' }}>
                      {option.name}
                    </div>
                    <div style={{ fontSize: '14px', color: '#666' }}>
                      {option.description}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Selected Pricing Details */}
            <div style={{
              background: '#f8f9fa',
              padding: '2rem',
              borderRadius: '12px',
              marginBottom: '2rem'
            }}>
              <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '1rem', color: '#1a1a1a' }}>
                {selected.name} Includes:
              </h3>
              <div style={{ marginBottom: '1.5rem' }}>
                {selected.features.map((feature, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    marginBottom: '0.75rem',
                    fontSize: '15px',
                    color: '#333'
                  }}>
                    <span style={{ color: '#0EA5E9', marginRight: '0.75rem', fontSize: '18px' }}>✓</span>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>

              <a
                href={selected.stripeLink}
                onClick={() => handleRegisterClick(selectedPricing)}
                style={{
                  display: 'block',
                  background: 'linear-gradient(135deg, #0EA5E9 0%, #0F172A 100%)',
                  color: 'white',
                  textDecoration: 'none',
                  padding: '16px 32px',
                  fontSize: '18px',
                  fontWeight: '700',
                  borderRadius: '8px',
                  textAlign: 'center',
                  cursor: 'pointer'
                }}
              >
                Register Now - {selected.price} →
              </a>

              {selectedPricing === 'full' && (
                <p style={{
                  fontSize: '14px',
                  color: '#666',
                  marginTop: '1rem',
                  textAlign: 'center'
                }}>
                  Payment plans available - contact learn@teachtappy.com
                </p>
              )}
            </div>
          </div>

          {/* About the Instructor */}
          <div style={{
            background: '#f8f9fa',
            padding: '2rem',
            borderRadius: '12px',
            marginBottom: '2rem'
          }}>
            <h2 style={{
              fontSize: '20px',
              fontWeight: '700',
              marginBottom: '1rem',
              color: '#1a1a1a'
            }}>
              About the Instructor
            </h2>
            <p style={{ fontSize: '15px', color: '#333', lineHeight: 1.6, marginBottom: '0.5rem' }}>
              <strong>Steve Galbraith</strong> - Educator and parent with over 15 years experience in education and AI technology
            </p>
            <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>
              learn@teachtappy.com • 813-593-8043
            </p>
          </div>

          {/* FAQ */}
          <div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '700',
              marginBottom: '1.5rem',
              color: '#1a1a1a',
              textAlign: 'center'
            }}>
              Common Questions
            </h2>

            <div style={{
              display: 'grid',
              gap: '1rem'
            }}>
              {[
                {
                  q: 'What if my child isn\'t tech-savvy?',
                  a: 'Perfect! We start from scratch. Mira uses voice (like talking to Siri), making it easier than typing. No coding required.'
                },
                {
                  q: 'Is this just homework help?',
                  a: 'No. We teach AI literacy skills that happen to make homework easier. Think "teach to fish" not "give a fish."'
                },
                {
                  q: 'Will my child use this to cheat?',
                  a: 'We explicitly teach ethical AI use. Students learn to use AI for understanding, not answers. Critical thinking is the focus.'
                },
                {
                  q: 'Can I get a refund if it doesn\'t work out?',
                  a: 'Yes. If you\'re not satisfied within the first 2 sessions, we\'ll refund your full payment, no questions asked.'
                }
              ].map((item, index) => (
                <div key={index} style={{
                  padding: '1.5rem',
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px'
                }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '0.5rem', color: '#1a1a1a' }}>
                    {item.q}
                  </h3>
                  <p style={{ fontSize: '14px', color: '#666', margin: 0, lineHeight: 1.6 }}>
                    {item.a}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{ textAlign: 'center', marginTop: '3rem', paddingBottom: '2rem', color: '#666', fontSize: '14px' }}>
        <p style={{ marginBottom: '0.5rem' }}>
          AI Learning Lab powered by <strong>Teach Tappy</strong>
        </p>
        <p style={{ margin: 0 }}>
          © 2026 Tappy Labs • <a href="https://teachtappy.ai" style={{ color: '#0EA5E9', textDecoration: 'none' }}>teachtappy.ai</a>
        </p>
      </div>
    </div>
  );
};

export default AILearningLabLanding;
